import Axios, { AxiosError, CancelTokenSource } from 'axios';
import { useState } from 'react';
import AxiosHelper from 'services/AxiosHelper';
import { useAxiosPaginatedLoader, UseAxiosPaginatedLoaderResult } from 'shared/hooks/useAxiosPaginatedLoader';
import useRocReCaptchaToken from 'shared/hooks/useRocReCaptchaToken';
import { FormValidationPromise, PaginatedResult } from 'shared/types';
import { handleAxiosErrorWithValidation } from 'shared/util/Misc';
import { ReviewSummaryDto } from '../product-details/types';
import { ReviewPostDto } from './types';
import { SortByDirection } from '../../../../../Roc.SharedClientApp/types';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';

/**
 * useProductReviews hook params object
 */
interface UseProductReviewsParams {
	/** The ID of the product, the source of the reviews. */
	productId: string;
	/** A summary of the reviews containing the average rating and number of visible reviews. */
	reviewsSummary: ReviewSummaryDto;
}

/**
 * useProductReviews hook result object
 */
interface UseProductReviewsResult extends UseAxiosPaginatedLoaderResult<ReviewPostDto, PaginatedResult<ReviewPostDto>> {
	isSaving: boolean;
	productReviewsError: Error | AxiosError | null;
	hasValidationError: boolean;
	isAbuseSaved: { [id: string]: boolean } | null;
	reviewAdded: boolean;
	loadMoreClicked: boolean;
	showWriteReview: boolean;
	reportAbuse: (reviewId: string) => Promise<void>;
	saveReview: (formValues: ReviewPostDto) => FormValidationPromise<void>;
	onLoadMoreClick: () => void;
	setProductReviewsError: (productReviewsError: Error | AxiosError | null) => void;
	setValidationHasError: (hasValidationError: boolean) => void;
	setReviewAdded: (review: boolean) => void;
	setShowWriteReview: (prop: boolean) => void;
}

const initialPageSize = 2;

/**
 * Custom hook for product reviews
 */
export default function useProductReviews({
	productId,
	reviewsSummary: { reviewCount },
}: UseProductReviewsParams): UseProductReviewsResult {
	const [isSaving, setIsSaving] = useState(false);
	const [productReviewsError, setProductReviewsError] = useState<Error | AxiosError | null>(null);
	const [hasValidationError, setValidationHasError] = useState(false);

	// storing these by id
	const [isAbuseSaved, setIsAbuseSaved] = useState<{ [id: string]: boolean } | null>(null);
	const [reviewAdded, setReviewAdded] = useState(false);
	const [loadMoreClicked, setLoadMoreClicked] = useState(reviewCount <= initialPageSize);

	const {
		page,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		setSorting,
		setPageSize,
		error,
		loading,
		results,
		sortByName,
		sortDirection,
	} = useAxiosPaginatedLoader<ReviewPostDto, PaginatedResult<ReviewPostDto>>({
		axiosParams: {
			url: `/ajax/products/${productId}/reviews`,
		},
		initialPageSize,
		initialSortByName: 'CreateDate',
		initialSortDirection: SortByDirection.Descending,
		cache: true,
	});

	const writeReviewRecaptcha = useRocReCaptchaToken('WriteReview');

	const [showWriteReview, setShowWriteReview] = useState(false);

	/**
	 * Report Abuse
	 */
	const reportAbuse = async (reviewId: string): Promise<void> => {
		setIsSaving(true);

		const requestToken: CancelTokenSource = Axios.CancelToken.source();

		try {
			await AxiosHelper.post(`/ajax/products/${productId}/reviews/abuse/${reviewId}`, {
				cancelToken: requestToken.token,
			});

			const newIsAbuseSaved = { ...isAbuseSaved };
			newIsAbuseSaved[`${reviewId}`] = true;

			setIsAbuseSaved(newIsAbuseSaved);
			setProductReviewsError(null);
		} catch (error) {
			if (Axios.isCancel(error)) {
				// if the component was unmounted, there's nothing to do
				return;
			}

			setProductReviewsError(error);

			console.error('An error occurred while saving abuse.', error);
		} finally {
			setIsSaving(false);
		}
	};

	/**
	 * Save Review
	 * @param formValues
	 */
	const saveReview = async (formValues: ReviewPostDto): FormValidationPromise<void> => {
		setIsSaving(true);

		const requestToken: CancelTokenSource = Axios.CancelToken.source();

		try {
			const token = await writeReviewRecaptcha.getToken();
			const payload = { ...formValues, reCaptchaToken: token };

			await AxiosHelper.post(`/ajax/products/${productId}/reviews`, payload, {
				cancelToken: requestToken.token,
			});

			setValidationHasError(false);
			setProductReviewsError(null);
			setShowWriteReview(false);
			setReviewAdded(true);

			if (formValues.rating) {
				dispatchRocEvent('product-rated', { productId: formValues.productId, rating: formValues.rating });
			}
		} catch (error) {
			if (Axios.isCancel(error)) {
				// if the component was unmounted, there's nothing to do
				return;
			}

			const axiosError = error as AxiosError;
			const responseCode = axiosError?.response?.status;
			const responseData = axiosError?.response?.data;

			if (responseCode === 400 && responseData != null) {
				setValidationHasError(true);
				setProductReviewsError(null);
			} else {
				// Generic error. We're not quite sure what happened
				setProductReviewsError(error);
				setValidationHasError(false);
			}

			if (responseCode === 400) {
				writeReviewRecaptcha.invalidateToken();
			}

			if (axiosError) {
				return handleAxiosErrorWithValidation(axiosError, 'An error occurred while saving.');
			}
		} finally {
			setIsSaving(false);
		}
	};

	/**
	 * Load the whole first page on show more click
	 */
	const onLoadMoreClick = async () => {
		setPageSize(10);
		setLoadMoreClicked(true);
	};

	return {
		isSaving,
		productReviewsError: productReviewsError,
		hasValidationError,
		setProductReviewsError: setProductReviewsError,
		setValidationHasError,
		isAbuseSaved,
		reviewAdded,
		setReviewAdded,
		loadMoreClicked,
		page,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		setSorting,
		setPageSize,
		error,
		loading,
		results,
		reportAbuse,
		saveReview,
		onLoadMoreClick,
		sortByName,
		sortDirection,
		showWriteReview,
		setShowWriteReview,
	};
}
